import { useAuth } from '../composables/useAuth';
import { logger } from '@maso-dj/shared-types';
import { StatusCodes } from 'http-status-codes';

/**
 * Makes an authenticated fetch request by automatically adding the Authorization header
 * Handles token expiry by logging out the user on 401 errors
 * @param url The URL to fetch
 * @param options Standard fetch options
 * @returns Promise with the fetch response
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const auth = useAuth();
  const token = await auth.getToken();

  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // CRITICAL: Send cookies with cross-origin requests (for session persistence)
  });

  // Handle 401 Unauthorized errors - token expired or invalid
  // Only logout on 401 with Bearer authentication challenge
  if (response.status === StatusCodes.UNAUTHORIZED && !auth.isLocalMode.value) {
    const wwwAuth = response.headers.get('www-authenticate');

    // Check if this is actually an auth error (Bearer authentication required)
    if (wwwAuth && wwwAuth.includes('Bearer')) {
      logger.warn('Authentication token expired or invalid. Logging out...');

      // Log the user out to clear the invalid session
      await auth.logout();
    }
  }

  return response;
}
